import React, { useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { COLORS, Text, Textarea } from '@clutter/clean';

import { AnchorButton, Button as PortalButton } from '@portal/components/shared/button';
import { TrackedLink } from '@portal/components/wt/tracked_link';
import heroImage from '@portal/images/csat_order_review_hero.png';
import { Rating, Review__ReviewableEnum, Status, useBuildReviewMutation } from '@portal/schema';

/** Figma 698:1283 — high-path CTA directs to Trustpilot. */
export const ORDER_CSAT_TRUSTPILOT_REVIEW_URL = 'https://www.trustpilot.com/review/clutter.com';

/** Order CSAT (1–5): Figma high path is 4–5 stars; low path is 3 or below. */
export const ORDER_CSAT_HIGH_SCORE_MIN = 4;

export const orderCsatIsHighScore = (score: number): boolean => score >= ORDER_CSAT_HIGH_SCORE_MIN;

/** Figma 719:222 / 719:229 — Button/Large primary */
const ORDER_CSAT_BUTTON_BG = '#0b7a7a';
const ORDER_CSAT_BUTTON_TEXT = '#f6f7f5';

const orderCsatPrimaryButtonStyles = css`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 16px 24px;
    border-radius: 8px;
    font-size: 16px;
    line-height: 16px;
    font-weight: 600;
    white-space: nowrap;
    color: ${ORDER_CSAT_BUTTON_TEXT};
    background: ${ORDER_CSAT_BUTTON_BG};
    border: 1px solid ${ORDER_CSAT_BUTTON_BG};

    &:not(:disabled):hover {
      color: ${ORDER_CSAT_BUTTON_TEXT};
      background: ${ORDER_CSAT_BUTTON_BG};
      border-color: ${ORDER_CSAT_BUTTON_BG};
    }
  }
`;

const ShareExperienceButton = styled(AnchorButton)`
  ${orderCsatPrimaryButtonStyles}
`;

const SubmitFeedbackButton = styled(PortalButton)`
  ${orderCsatPrimaryButtonStyles}
`;

const Content = styled.div`
  max-width: 964px;
  margin: 0 auto;
  width: 100%;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 60px;
  align-items: flex-start;
`;

const CopyBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  align-items: flex-start;
  width: 100%;
`;

/** Figma Headers/H1 — Recoleta 45 / 1.2 */
const PageTitle = styled(Text.Title)`
  font-size: 45px;
  line-height: 1.2;
  font-weight: 500;
  margin: 0;
  width: 100%;
`;

/** Figma Paragraph/Body Large — Basier 24 / moss */
const BodyLarge = styled(Text.Body)`
  font-size: 24px;
  line-height: 1.2;
  color: ${COLORS.tealDark};
  margin: 0;
  width: 100%;
`;

const Hero = styled.img`
  width: 100%;
  max-height: 533px;
  object-fit: cover;
  border-radius: 12px;
  display: block;
`;

const LowFormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  align-items: center;
`;

const FeedbackInputShell = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
  min-height: 184px;
  padding: 18px;
  border: 1.577px solid #ebeced;
  border-radius: 12px;
  background: ${COLORS.cloud};
  box-sizing: border-box;
`;

const EditIcon = styled(FontAwesomeIcon)`
  color: #757575;
  font-size: 20px;
  margin-top: 2px;
  flex-shrink: 0;
`;

const FeedbackField = styled(Textarea)`
  flex: 1;
  min-height: 140px;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  resize: vertical;
  font-size: 24px;
  line-height: 1.2;
  background: transparent;
  box-shadow: none;

  &:focus {
    outline: none;
    box-shadow: none;
  }

  &::placeholder {
    color: #757575;
  }
`;

const SubmitRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const ErrorMessage = styled(Text.Callout)`
  color: ${COLORS.toucan};
  width: 100%;
  margin-top: 12px;
`;

const LowRatingThankYou: React.FC = () => (
  <Content>
    <Section>
      <CopyBlock>
        <PageTitle size="large">Feedback submitted. Thank you!</PageTitle>
        <BodyLarge>Our policy team will review what you shared and follow up if needed.</BodyLarge>
      </CopyBlock>
    </Section>
  </Content>
);

const HighRating: React.FC = () => (
  <Content>
    <Section>
      <Hero src={heroImage} alt="" />
      <CopyBlock>
        <PageTitle size="large">We are so glad we kept your items safe.</PageTitle>
        <BodyLarge>
          If you have a brief moment to share your experience publicly, it helps our care team immensely.
        </BodyLarge>
        <TrackedLink
          params={{
            pageName: 'portal:order_csat_review',
            container: 'share_experience',
            action: 'click',
            objectType: 'link',
            objectName: 'trustpilot_review',
            label: 'Share your experience',
            value: ORDER_CSAT_TRUSTPILOT_REVIEW_URL,
          }}
        >
          <ShareExperienceButton
            kind="primary"
            size="medium"
            href={ORDER_CSAT_TRUSTPILOT_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Share your experience
          </ShareExperienceButton>
        </TrackedLink>
      </CopyBlock>
    </Section>
  </Content>
);

const LowRating: React.FC<{
  reviewableID: string;
  score: number;
  kind?: string;
  schedulingID?: string;
}> = ({ reviewableID, score, kind, schedulingID }) => {
  const [comments, setComments] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [submitted, setSubmitted] = useState(false);
  const [buildReview, { loading: building }] = useBuildReviewMutation();

  if (submitted) {
    return <LowRatingThankYou />;
  }

  const onSubmit = async () => {
    setError(undefined);
    const response = await buildReview({
      variables: {
        input: {
          reviewable: { id: reviewableID, type: Review__ReviewableEnum.Order },
          score,
          comments,
          kind,
          schedulingID,
        },
      },
    });
    if (response?.data?.buildReview?.status === Status.Unprocessable) {
      setError(`There was a problem submitting your review: ${response.data.buildReview.error}`);
      return;
    }
    if (response?.data?.buildReview?.status === Status.Ok) {
      setSubmitted(true);
    }
  };

  return (
    <Content>
      <Section>
        <CopyBlock>
          <PageTitle size="large">We are deeply sorry we fell short of our protection standards.</PageTitle>
          <BodyLarge>Please tell us what happened so our policy team can fix it.</BodyLarge>
        </CopyBlock>
        <LowFormSection>
          <div style={{ width: '100%' }}>
            <FeedbackInputShell>
              <EditIcon icon={faPen} aria-hidden />
              <FeedbackField
                name="order_csat_comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="What would you like to share?"
              />
            </FeedbackInputShell>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </div>
          <SubmitRow>
            <SubmitFeedbackButton
              onClick={onSubmit}
              disabled={building}
              loading={building}
              kind="primary"
              size="medium"
            >
              Submit feedback
            </SubmitFeedbackButton>
          </SubmitRow>
        </LowFormSection>
      </Section>
    </Content>
  );
};

export const OrderCsat: React.FC<{
  signedReviewableID: string;
  reviewableID: string;
  score?: number;
  rating?: Rating;
  kind?: string;
  schedulingID?: string;
}> = ({ reviewableID, score, rating, kind, schedulingID }) => {
  const showHigh = score !== undefined && !Number.isNaN(score) ? orderCsatIsHighScore(score) : rating === Rating.Good;

  if (score === undefined && rating !== Rating.Good && rating !== Rating.Poor) {
    return <ErrorMessage>That link is missing a rating.</ErrorMessage>;
  }

  if (showHigh) {
    return <HighRating />;
  }

  const lowScore = score ?? (rating === Rating.Poor ? 3 : undefined);
  if (lowScore === undefined || Number.isNaN(lowScore)) {
    return <ErrorMessage>That link is missing a rating.</ErrorMessage>;
  }

  return <LowRating reviewableID={reviewableID} score={lowScore} kind={kind} schedulingID={schedulingID} />;
};
